const TRANSITION_WINDOWS = [
  [0.09, 0.13],
  [0.205, 0.245],
  [0.325, 0.365],
  [0.455, 0.495],
  [0.585, 0.625],
  [0.705, 0.745],
  [0.835, 0.875],
] as const;

/** Stable points where each of the eight story stages is fully readable. */
export const HOME_BISPLATE_STAGE_PROGRESS = [
  0.035,
  0.165,
  0.285,
  0.415,
  0.545,
  0.665,
  0.785,
  0.94,
] as const;

const PROCESS_TRAVEL_KEYFRAMES = [
  { progress: 0, value: 0.36 },
  { progress: 0.17, value: 0.36 },
  { progress: 0.65, value: -0.46 },
  { progress: 0.78, value: -0.46 },
  { progress: 0.88, value: 0 },
] as const;

export interface HomeBisplateTimeline {
  progress: number;
  stageWeights: number[];
  stageOpacities: number[];
  stageShifts: number[];
  heat: number;
  quench: number;
  quenchFront: number;
  temper: number;
  inspection: number;
  processAlignment: number;
  processTravel: number;
}

export function clampHomeBisplateProgress(value: number) {
  return Math.min(1, Math.max(0, value));
}

function smoothstep(value: number) {
  const progress = clampHomeBisplateProgress(value);
  return progress * progress * (3 - 2 * progress);
}

export function smoothHomeBisplateRange(
  progress: number,
  start: number,
  end: number,
) {
  return smoothstep((progress - start) / Math.max(0.001, end - start));
}

function sampleProcessTravel(progress: number) {
  if (progress <= PROCESS_TRAVEL_KEYFRAMES[0].progress) {
    return PROCESS_TRAVEL_KEYFRAMES[0].value;
  }

  for (let index = 0; index < PROCESS_TRAVEL_KEYFRAMES.length - 1; index += 1) {
    const start = PROCESS_TRAVEL_KEYFRAMES[index];
    const end = PROCESS_TRAVEL_KEYFRAMES[index + 1];
    if (progress <= end.progress) {
      const amount = smoothHomeBisplateRange(
        progress,
        start.progress,
        end.progress,
      );
      return start.value + (end.value - start.value) * amount;
    }
  }

  return PROCESS_TRAVEL_KEYFRAMES[PROCESS_TRAVEL_KEYFRAMES.length - 1].value;
}

export function sampleHomeBisplateTimeline(
  rawProgress: number,
): HomeBisplateTimeline {
  const progress = clampHomeBisplateProgress(rawProgress);
  const transitions = TRANSITION_WINDOWS.map(([start, end]) =>
    smoothHomeBisplateRange(progress, start, end),
  );
  const stageWeights = [
    1 - transitions[0],
    transitions[0] * (1 - transitions[1]),
    transitions[1] * (1 - transitions[2]),
    transitions[2] * (1 - transitions[3]),
    transitions[3] * (1 - transitions[4]),
    transitions[4] * (1 - transitions[5]),
    transitions[5] * (1 - transitions[6]),
    transitions[6],
  ];
  const incomingOpacities = transitions.map((transition) =>
    smoothstep((transition - 0.3) / 0.7),
  );
  const outgoingOpacities = transitions.map(
    (transition) => 1 - smoothstep(transition / 0.7),
  );
  const stageOpacities = stageWeights.map((_, index) => {
    const incoming = index === 0 ? 1 : incomingOpacities[index - 1];
    const outgoing =
      index === stageWeights.length - 1 ? 1 : outgoingOpacities[index];
    return incoming * outgoing;
  });
  const stageShifts = stageWeights.map((_, index) => {
    const incoming = index === 0 ? 1 : transitions[index - 1];
    const outgoing = index === stageWeights.length - 1 ? 0 : transitions[index];
    return 1 - incoming - outgoing;
  });
  const heat =
    smoothHomeBisplateRange(progress, 0.205, 0.245) *
    (1 - smoothHomeBisplateRange(progress, 0.325, 0.345));
  const quench =
    smoothHomeBisplateRange(progress, 0.345, 0.365) *
    (1 - smoothHomeBisplateRange(progress, 0.455, 0.475));
  const temper =
    smoothHomeBisplateRange(progress, 0.475, 0.495) *
    (1 - smoothHomeBisplateRange(progress, 0.585, 0.605));
  const inspection =
    smoothHomeBisplateRange(progress, 0.605, 0.625) *
    (1 - smoothHomeBisplateRange(progress, 0.705, 0.725));
  const processAlignment =
    smoothHomeBisplateRange(progress, 0.18, 0.235) *
    (1 - smoothHomeBisplateRange(progress, 0.625, 0.705));

  return {
    progress,
    stageWeights,
    stageOpacities,
    stageShifts,
    heat,
    quench,
    quenchFront: smoothHomeBisplateRange(progress, 0.365, 0.455),
    temper,
    inspection,
    processAlignment,
    processTravel: sampleProcessTravel(progress),
  };
}

export function getHomeBisplateActiveStage(weights: number[]) {
  return weights.reduce(
    (best, value, index) => (value > weights[best] ? index : best),
    0,
  );
}
